// Recognise URLs that have a known inline embed format. Returns the iframe `src`
// and a default aspect ratio so the canvas card can size itself appropriately.
// Plain URLs (`kind: 'plain'`) fall through to the regular LinkCard rendering.
//
// Coverage chosen for v1: YouTube (videos), Spotify (audio), Pinterest pins
// (visual reference). Twitter / X, Instagram, TikTok, Vimeo all need their
// vendor widget JS or have heavy embed restrictions; we keep those as plain
// link cards for now.
//
// Notes on what does NOT classify here:
//   - `pin.it/...` Pinterest short URLs hide the pin id behind a server-side
//     redirect. Falls through to the plain link card. Users who paste the long
//     form (`de.pinterest.com/pin/<id>` etc.) get the embed.
//   - Music videos and other Vevo / label-protected YouTube content may still
//     fail to embed (player returns a "configuration error"). The classifier
//     returns the embed URL anyway — there's no reliable way to detect this
//     without actually loading the video.

export type UrlEmbed =
  | { kind: 'plain' }
  | {
      kind: 'youtube' | 'spotify' | 'pinterest'
      embedUrl: string
      // Aspect ratio as width / height; card height picks a sensible default
      // from this so video embeds aren't squashed.
      aspectRatio: number
    }

export function classifyUrl(rawUrl: string): UrlEmbed {
  let url: URL
  try {
    url = new URL(rawUrl)
  } catch {
    return { kind: 'plain' }
  }

  const host = url.hostname.replace(/^www\./, '')

  // YouTube — watch / youtu.be / shorts / embed. Preserves start time + playlist
  // and uses the youtube-nocookie domain for better privacy + slightly more
  // permissive embed restrictions on some content.
  if (host === 'youtube.com' || host === 'm.youtube.com') {
    if (url.pathname === '/watch') {
      const v = url.searchParams.get('v')
      if (v) return ytEmbed(v, url.searchParams)
    }
    if (url.pathname.startsWith('/shorts/')) {
      const v = url.pathname.split('/')[2]
      if (v) return ytEmbed(v, url.searchParams)
    }
    if (url.pathname.startsWith('/embed/')) {
      const v = url.pathname.split('/')[2]
      if (v) return ytEmbed(v, url.searchParams)
    }
  }
  if (host === 'youtu.be') {
    const v = url.pathname.replace(/^\//, '').split('/')[0]
    if (v) return ytEmbed(v, url.searchParams)
  }

  // Spotify — open.spotify.com/<type>/<id>.
  if (host === 'open.spotify.com') {
    const parts = url.pathname.replace(/^\//, '').split('/')
    const type = parts[0]
    const id = parts[1]
    if (type && id && ['track', 'album', 'playlist', 'episode', 'show', 'artist'].includes(type)) {
      // Spotify's compact embed is wider than tall; track embeds are ~80px tall
      // but iframes need a min height to render the controls, so we use 4:1.
      return {
        kind: 'spotify',
        embedUrl: `https://open.spotify.com/embed/${type}/${id}`,
        aspectRatio: type === 'track' ? 4 : 1,
      }
    }
  }

  // Pinterest — pinterest.com/pin/<id>. Accepts country subdomains
  // (de.pinterest.com etc) but NOT the pin.it short links.
  if (host === 'pinterest.com' || host.endsWith('.pinterest.com')) {
    const match = url.pathname.match(/^\/pin\/(\d+)\/?$/)
    if (match) {
      return {
        kind: 'pinterest',
        embedUrl: `https://assets.pinterest.com/ext/embed.html?id=${match[1]}`,
        // Pinterest pins are typically portrait; default to 3:4.
        aspectRatio: 3 / 4,
      }
    }
  }

  return { kind: 'plain' }
}

function ytEmbed(videoId: string, params: URLSearchParams): UrlEmbed {
  const embedParams = new URLSearchParams()
  const start = parseYoutubeTime(params.get('t') ?? params.get('start'))
  if (start !== null) embedParams.set('start', String(start))
  const list = params.get('list')
  // Skip auto-generated "Radio" lists (`RD...`) — they're tied to user state and
  // the embed often rejects them outright.
  if (list && !list.startsWith('RD')) embedParams.set('list', list)
  const qs = embedParams.toString()
  return {
    kind: 'youtube',
    embedUrl: `https://www.youtube-nocookie.com/embed/${videoId}${qs ? `?${qs}` : ''}`,
    aspectRatio: 16 / 9,
  }
}

// YouTube share URLs use a few timestamp formats: plain seconds (`120`),
// seconds with suffix (`120s`), or mixed units (`1h2m20s` / `2m20s` / `45s`).
// The embed URL's `start` parameter wants an integer second count.
function parseYoutubeTime(raw: string | null): number | null {
  if (!raw) return null
  const trimmed = raw.trim()
  if (/^\d+s?$/.test(trimmed)) return parseInt(trimmed, 10)
  const match = trimmed.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/)
  if (!match) return null
  const h = match[1] ? parseInt(match[1], 10) : 0
  const m = match[2] ? parseInt(match[2], 10) : 0
  const s = match[3] ? parseInt(match[3], 10) : 0
  const total = h * 3600 + m * 60 + s
  return total > 0 ? total : null
}
